import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, getUser, canClinical, clinicalLabel } from "../api";
import { Avatar } from "../components/Posts";
import { formatProName } from "../lib/proName";
import { toast } from "sonner";
import { PdfViewer } from "../components/PdfViewer";
import { ZoomableImage } from "../components/ZoomableImage";
import { Linkify } from "../components/Linkify";
import { createPortal } from "react-dom";
import { DaySeparator, MessageTicks, messageStatus, localDayKey, dayLabel, timeHHmm } from "../components/MessageMeta";

export default function Chat() {
  const { t, i18n } = useTranslation();
  const { otherId } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const [params, setParams] = useSearchParams();
  const u = getUser();
  const [convs, setConvs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [canSend, setCanSend] = useState(true);
  const [peer, setPeer] = useState(null); // interlocutore (per intestazione + link profilo)
  const [instant, setInstant] = useState(null); // { request, consult } — stato "Prenota ora"
  const [payConfirm, setPayConfirm] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [attachMenu, setAttachMenu] = useState(false);
  const [chatUploading, setChatUploading] = useState(false);
  const [picker, setPicker] = useState(null); // { files, q, sel:Set, sending, loading }
  const [viewer, setViewer] = useState(null); // { url, filename, contentType } — Blob locale, nessun token in URL
  const fileRef = useRef(null);
  const msgRef = useRef(null);
  // Etichetta interlocutore: professionista con appellativo (nome completo); paziente/altro solo nickname (privacy)
  const peerLabel = (o) => {
    if (!o) return "";
    if (o.role === "professional") return formatProName({ title: o.professional_title, first_name: o.first_name, last_name: o.last_name }, i18n.language) || o.nickname;
    return o.nickname || o.first_name || "";
  };
  const loadC = () => api.get("/chat/conversations").then(({data}) => setConvs(data));
  const loadM = () => { if (!otherId) return;
    api.get(`/chat/messages/${otherId}`).then(({data}) => { setMsgs(data.messages); setCanSend(data.can_send); setPeer(data.other || null); window.dispatchEvent(new Event("chat-updated")); }); };
  // Apre il profilo pubblico canonico dell'interlocutore senza selezionare/cambiare conversazione né inviare nulla
  const openProfile = (e, url) => { e.stopPropagation(); e.preventDefault(); if (url) nav(url); };
  useEffect(() => { loadC(); }, []);
  useEffect(() => { loadM(); const i = setInterval(loadM, 5000); return () => clearInterval(i); }, [otherId]);
  // Mostra sempre l'ultimo messaggio: all'apertura della conversazione e dopo invio/ricezione
  useEffect(() => { const el = msgRef.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs, otherId]);
  // Stato "Prenota ora": richiesta aperta / videoconsulto immediato confermato (polling per entrambi i ruoli)
  const loadInstant = () => { if (!otherId) { setInstant(null); return; } api.get(`/instant/active/${otherId}`).then(({data}) => setInstant(data)).catch(() => setInstant(null)); };
  useEffect(() => { loadInstant(); const i = setInterval(loadInstant, 5000); return () => clearInterval(i); }, [otherId]);
  // Messaggio precompilato (modificabile) proveniente dal pulsante "Prenota ora"
  useEffect(() => { if (loc.state?.prefill) setText(loc.state.prefill); }, [loc.state]);
  // Ritorno dal pagamento immediato: forza il settlement e aggiorna lo stato
  useEffect(() => {
    const sid = params.get("instant_paid");
    if (!sid) return;
    api.get(`/payments/status/${sid}`).catch(() => {}).finally(() => { loadInstant(); loadM(); const p = new URLSearchParams(params); p.delete("instant_paid"); setParams(p, { replace: true }); });
  }, [params]);
  const payConnect = async () => {
    if (!instant?.request) return;
    setPayBusy(true);
    try {
      const { data } = await api.post(`/instant/${instant.request.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "request_expired" ? t("instant.expired") : (typeof d === "string" ? d : t("events.error_generic")));
      setPayConfirm(false); loadInstant();
    } finally { setPayBusy(false); }
  };
  useEffect(() => () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); }, [viewer]);
  const send = async () => {
    if (!text.trim() || !otherId) return;
    try { await api.post("/chat/send", { to_user_id: otherId, body: text }); setText(""); loadM(); }
    catch(e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };
  // Blocco consentito solo a pro/struttura verso un paziente molesto (enforcement anche lato backend).
  const blockPeer = async () => {
    if (!otherId) return;
    if (!window.confirm(t("followx.block_confirm"))) return;
    try {
      const { data } = await api.post(`/users/${otherId}/block`);
      if (data.contact_support) toast.warning(t("followx.block_support"));
      else { toast.success(t("followx.blocked")); loadM(); loadC(); }
    } catch (e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file || !otherId || chatUploading) return;
    const fd = new FormData(); fd.append("to_user_id", otherId); fd.append("file", file);
    setChatUploading(true);
    try { await api.post("/chat/attachments/upload", fd); loadM(); }
    catch(err) { toast.error(err.response?.data?.detail || t("events.error_generic")); }
    finally { setChatUploading(false); e.target.value = ""; }
  };
  // --- Scegli da YouPAM Drive (solo file propri, non cestinati; lato server copia protetta) ---
  const openPicker = () => {
    setAttachMenu(false);
    setPicker({ files: [], q: "", sel: new Set(), sending: false, loading: true });
    api.get("/drive").then(({data}) => setPicker(p => p && { ...p, files: data, loading: false }))
      .catch(() => { setPicker(null); toast.error(t("drive.open_error")); });
  };
  const toggleSel = (id) => setPicker(p => { const s = new Set(p.sel); s.has(id) ? s.delete(id) : s.add(id); return { ...p, sel: s }; });
  const attachFromDrive = async () => {
    if (!picker || picker.sel.size === 0 || !otherId) return;
    setPicker(p => ({ ...p, sending: true }));
    const batch = crypto.randomUUID();  // un invio multi-allegato = un solo badge/email
    try {
      for (const fid of picker.sel) {
        const fd = new FormData(); fd.append("to_user_id", otherId); fd.append("batch_id", batch);
        await api.post(`/drive/${fid}/send-to-chat`, fd);
      }
      setPicker(null); loadM();
    } catch(err) { setPicker(p => p && { ...p, sending: false }); toast.error(err.response?.data?.detail || t("events.error_generic")); }
  };
  // --- Apertura/download allegati: fetch autenticato con header, Blob protetto ---
  const fetchBlob = async (url) => {
    const { data, headers } = await api.get(url, { responseType: "blob" });
    const ct = (headers["content-type"] || data.type || "application/octet-stream").split(";")[0];
    return new Blob([data], { type: ct });
  };
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const openAtt = async (aid, filename) => {
    try {
      const blob = await fetchBlob(`/chat/attachments/${aid}/view`);
      if (blob.type === "application/pdf") setViewer({ blob, filename, contentType: blob.type }); // pdf.js interno
      else if (blob.type.startsWith("image/") || blob.type.startsWith("video/")) setViewer({ url: URL.createObjectURL(blob), filename, contentType: blob.type });
      else downloadBlob(blob, filename);
    } catch (e) {
      const st = e?.response?.status;
      toast.error(st === 401 || st === 403 ? t("pdfx.err_forbidden") : st === 404 ? t("pdfx.err_missing") : t("drive.open_error"));
    }
  };
  const downloadAtt = async (aid, filename) => {
    try { downloadBlob(await fetchBlob(`/chat/attachments/${aid}/view`), filename); }
    catch { toast.error(t("drive.open_error")); }
  };
  const saveToDrive = async (aid) => {
    try {
      const { data } = await api.post(`/chat/attachments/${aid}/save-to-drive`);
      toast.success(data.already ? t("chat.already_saved") : t("chat.saved_drive"));
    } catch { toast.error(t("events.error_generic")); }
  };
  const closeViewer = () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); setViewer(null); };
  const pickerFiles = picker ? picker.files.filter(f => f.filename.toLowerCase().includes(picker.q.toLowerCase())) : [];
  return (<Layout><div className="yp-chat-wrap yp-container lg:py-8 lg:grid lg:grid-cols-3 lg:gap-4 lg:h-[75vh]">
    <div className={`yp-card p-3 overflow-y-auto min-h-0 ${otherId ? "hidden lg:block" : "flex-1 lg:flex-none"}`} data-testid="chat-list">
      <h2 className="font-semibold mb-2">{t("chat.conversations")}</h2>
      {convs.length === 0 && <div className="text-sm text-slate-400">{t("chat.empty_conv")}</div>}
      {convs.map(c => (
        <button key={c.conv} type="button" onClick={()=>nav(`/chat/${c.other?.id}`)} className="w-full text-left p-2 hover:bg-slate-50 rounded flex items-center gap-2" data-testid="chat-conv-item">
          <Avatar url={c.other?.avatar_url} label={c.other?.nickname || c.other?.first_name} cls="w-8 h-8 text-xs" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm flex items-center gap-2">{peerLabel(c.other)}
              {c.unread > 0 && <span className="yp-badge yp-badge-approved !px-1.5 !py-0 text-[10px] leading-4" data-testid="chat-conv-unread" role="status">{c.unread}</span>}</div>
            <div className="text-xs text-slate-400 truncate">{c.last?.body}</div>
          </div>
        </button>))}
    </div>
    <div className={`yp-card p-3 lg:col-span-2 flex-col min-h-0 ${otherId ? "flex flex-1" : "hidden lg:flex"}`}>
      {!otherId && <div className="text-slate-400 text-center my-auto">{t("chat.select_conv")}</div>}
      {otherId && (<>
        {peer && (
          <div className="flex items-center gap-2 pb-3 mb-1 border-b border-slate-100" data-testid="chat-header">
            <button type="button" onClick={()=>nav('/chat')} className="lg:hidden p-2 -ml-1 rounded hover:bg-slate-100 min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label={t('hbio.back')} data-testid="chat-back-btn"><ArrowLeft size={20}/></button>
            <Avatar url={peer.avatar_url} label={peer.nickname || peer.first_name} cls="w-9 h-9 text-xs shrink-0" />
            <span className="font-semibold truncate flex-1 min-w-0">{peerLabel(peer)}</span>
            {(peer.profile_url || peer.bio_url) && (
              <button type="button" onClick={()=>nav(peer.profile_url || peer.bio_url)} className="yp-btn yp-btn-outline !py-1.5 !px-3 shrink-0 min-h-[44px]" data-testid={peer.profile_url ? "chat-open-profile" : "chat-open-bio"}>
                {peer.profile_url ? t('hbio.public_profile') : (u?.role === "organization" ? t('followx.bio') : t('hbio.health_bio'))}
              </button>)}
            {canClinical(u) && peer.role === "user" && (
              <button type="button" onClick={()=>nav(`/clinica/${otherId}`)} className="yp-btn yp-btn-outline !py-1.5 !px-3 shrink-0 min-h-[44px]" data-testid="chat-clinical-btn">{clinicalLabel(u)}</button>)}
            {(u?.role === "professional" || u?.role === "organization") && peer.role === "user" && (
              <button type="button" onClick={blockPeer} className="yp-btn yp-btn-ghost text-red-600 !py-1.5 !px-3 shrink-0 min-h-[44px]" data-testid="chat-block-btn">{t("followx.block")}</button>)}
          </div>)}
        {instant && (instant.consult || instant.request) && (
          <div className="mb-3 p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-between gap-2 flex-wrap" data-testid="instant-banner">
            {instant.consult ? (<>
              <span className="text-sm text-blue-800">{t("instant.enter_room")}</span>
              <button className="yp-btn yp-btn-primary !py-1.5" onClick={()=>nav(`/video/${instant.consult.id}`)} data-testid="instant-enter-room">{t("bookings.enter_room")}</button>
            </>) : (instant.request && new Date(instant.request.expires_at).getTime() > Date.now()) ? (<>
              <span className="text-sm text-blue-800">{instant.pay_enabled ? t("instant.pay_hint") : t("instant.pay_disabled")}</span>
              <button className="yp-btn yp-btn-primary !py-1.5 disabled:opacity-50 disabled:cursor-not-allowed" onClick={()=>setPayConfirm(true)} disabled={!instant.pay_enabled} data-testid="pay-connect-btn">{t("instant.pay_connect")}</button>
            </>) : (
              <span className="text-sm text-slate-500" data-testid="instant-expired">{t("instant.expired")}</span>
            )}
          </div>)}
        <div ref={msgRef} className="flex-1 min-h-0 overflow-y-auto space-y-2 mb-3" data-testid="chat-messages">
          {msgs.map((m, i) => {
            const prev = msgs[i-1];
            const showSep = !prev || localDayKey(prev.at) !== localDayKey(m.at);
            const mine = m.from_id === u?.id;
            const status = messageStatus(m, u?.id);
            return (<div key={m.id}>
              {showSep && <DaySeparator label={dayLabel(m.at, t, i18n.language)} />}
              <div className={`max-w-[80%] p-2 rounded-xl ${mine?"ml-auto bg-blue-500 text-white":"bg-slate-100"}`}>
                {m.type === "attachment" ? (
                  <div>
                    <div className="text-xs opacity-70">📎 {m.body} {m.read ? `· ${t("chat.read")}` : `· ${t("chat.unread")}`}</div>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      <button className="text-xs underline" onClick={()=>openAtt(m.attachment_id, m.body)} data-testid="chat-att-view">
                        {u?.role === "professional" ? t("chat.view_only") : t("chat.open")}
                      </button>
                      {["user","professional","organization"].includes(u?.role) && <button className="text-xs underline" onClick={()=>downloadAtt(m.attachment_id, m.body)} data-testid="chat-att-download">⬇ {t("chat.download")}</button>}
                      {u?.role === "user" && m.to_id === u.id && (
                        <button className="text-xs underline" onClick={()=>saveToDrive(m.attachment_id)} data-testid="chat-save-drive">{t("chat.save_drive")}</button>
                      )}
                    </div>
                  </div>
                ) : <div className="text-sm whitespace-pre-wrap break-words"><Linkify text={m.body} linkClassName={`underline break-all ${mine ? "text-white" : "text-blue-600"}`} /></div>}
                <div className={`flex items-center gap-1 justify-end mt-0.5 text-[10px] ${mine?"text-white/70":"text-slate-400"}`}>
                  <span data-testid="msg-time">{timeHHmm(m.at, i18n.language)}</span>
                  {mine && <MessageTicks status={status} t={t} />}
                </div>
              </div>
            </div>);
          })}
        </div>
        {canSend ? (
          <div className="flex gap-2 shrink-0">
            <input className="yp-input" value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder={t("chat.input_ph")} data-testid="chat-input" />
            <div className="relative shrink-0">
              <button className="yp-btn yp-btn-ghost disabled:opacity-50" onClick={()=>setAttachMenu(v=>!v)} disabled={chatUploading} data-testid="chat-upload">{chatUploading ? <span className="inline-block w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin align-middle" /> : "📎"}</button>
              {attachMenu && (
                <div className="absolute bottom-12 right-0 yp-card p-2 w-64 z-20 shadow-lg" data-testid="chat-attach-menu">
                  <button className="w-full text-left text-sm p-2 hover:bg-slate-50 rounded" onClick={()=>{ setAttachMenu(false); fileRef.current?.click(); }} data-testid="chat-attach-device">{t("chat.attach_device")}</button>
                  {u?.role === "user" && <button className="w-full text-left text-sm p-2 hover:bg-slate-50 rounded" onClick={openPicker} data-testid="chat-attach-drive">{t("chat.attach_drive")}</button>}
                </div>)}
              <input ref={fileRef} type="file" className="hidden" accept=".pdf,image/*,video/mp4,video/webm" onChange={upload} data-testid="chat-file-input" />
            </div>
            <button className="yp-btn yp-btn-primary" onClick={send} data-testid="chat-send-btn">{t("chat.send")}</button>
          </div>
        ) : <div className="text-sm text-slate-500 text-center p-3 bg-slate-50 rounded">{t("chat.read_only")}</div>}
      </>)}
    </div>
    {picker && (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>!picker.sending && setPicker(null)} data-testid="drive-picker-modal">
        <div className="yp-card p-4 w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e=>e.stopPropagation()}>
          <h3 className="font-bold mb-3">{t("chat.picker_title")}</h3>
          <input className="yp-input mb-3" placeholder={t("common.search")} value={picker.q} onChange={e=>setPicker(p=>({...p,q:e.target.value}))} data-testid="drive-picker-search" />
          <div className="flex-1 overflow-y-auto space-y-1">
            {picker.loading && <div className="text-sm text-slate-400">{t("common.loading")}</div>}
            {!picker.loading && pickerFiles.length === 0 && <div className="text-sm text-slate-400" data-testid="drive-picker-empty">{t("chat.picker_empty")}</div>}
            {pickerFiles.map(f => (
              <label key={f.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer" data-testid="drive-picker-item">
                <input type="checkbox" checked={picker.sel.has(f.id)} onChange={()=>toggleSel(f.id)} data-testid="drive-picker-check" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.filename}</div>
                  <div className="text-xs text-slate-500">{f.content_type} · {(f.size/1024).toFixed(1)} KB · {new Date(f.created_at).toLocaleDateString()}</div>
                </div>
              </label>))}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button className="yp-btn yp-btn-ghost" onClick={()=>setPicker(null)} disabled={picker.sending} data-testid="drive-picker-cancel">{t("common.cancel")}</button>
            <button className="yp-btn yp-btn-primary" onClick={attachFromDrive} disabled={picker.sel.size===0 || picker.sending} data-testid="drive-picker-attach">{picker.sending ? t("common.loading") : `${t("chat.attach")}${picker.sel.size>0?` (${picker.sel.size})`:""}`}</button>
          </div>
        </div>
      </div>)}
    {viewer?.contentType === "application/pdf" && <PdfViewer blob={viewer.blob} filename={viewer.filename} onClose={closeViewer} testid="chat-pdf-viewer" />}
    {viewer && viewer.contentType !== "application/pdf" && createPortal((
      <div className="fixed inset-0 z-[9998] bg-black/70 flex flex-col" onClick={closeViewer} data-testid="chat-att-viewer">
        <div className="flex items-center p-3 text-white" onClick={e=>e.stopPropagation()}>
          <span className="text-sm truncate">{viewer.filename}</span>
        </div>
        <div className="flex-1 flex items-center justify-center p-4 overflow-auto" onClick={e=>e.stopPropagation()}>
          <div className="relative inline-block max-w-full max-h-full">
            <button type="button" onClick={closeViewer} data-testid="chat-att-viewer-close" aria-label={t("video.chat_close")}
              className="absolute top-1 right-1 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-black/70 text-white text-xl leading-none shadow-lg active:scale-95">✕</button>
            {viewer.contentType?.startsWith("video/")
              ? <video src={viewer.url} controls autoPlay className="block max-w-full max-h-full rounded" data-testid="chat-att-viewer-video" />
              : <ZoomableImage src={viewer.url} alt={viewer.filename} testid="chat-att-viewer-image" />}
          </div>
        </div>
      </div>), document.body)}
    {payConfirm && (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>!payBusy && setPayConfirm(false)} data-testid="pay-confirm-modal">
        <div className="yp-card p-5 w-full max-w-md" onClick={e=>e.stopPropagation()}>
          <p className="text-sm text-slate-700">{t("instant.pay_warning")}</p>
          <div className="flex justify-end gap-2 mt-4">
            <button className="yp-btn yp-btn-ghost" onClick={()=>setPayConfirm(false)} disabled={payBusy} data-testid="pay-cancel-btn">{t("instant.cancel")}</button>
            <button className="yp-btn yp-btn-primary" onClick={payConnect} disabled={payBusy} data-testid="pay-confirm-btn">{payBusy ? t("common.loading") : t("instant.confirm_pay")}</button>
          </div>
        </div>
      </div>)}
  </div></Layout>);
}
